/* lib/carrilEstado.js — EN QUÉ ESTÁ EL CARRIL, Y QUIÉN VA A NAVEGAR (TANDA 17).
 *
 * ── QUÉ PROBLEMA RESUELVE ───────────────────────────────────────────────────
 * Tres piezas de la app miran el mismo carril desde afuera y hasta esta tanda
 * ninguna podía preguntarle nada:
 *
 *   · EL CURADOR (`lib/diag/carrilTripwire.js`, firma #201) plancha el
 *     `transform` del carril cuando lo encuentra puesto. Su juicio era CIEGO:
 *     un transform es ILEGÍTIMO en reposo y perfectamente LEGÍTIMO mientras un
 *     dedo lo está arrastrando, y sus dos gatillos no tenían forma de
 *     distinguirlo. Ahora preguntan aquí.
 *   · LA PÍLDORA (`components/MobileBottomNav.js`, firma #202) necesita saber
 *     si hay un aterrizaje EN VUELO para meter su tap en la misma cola de uno
 *     que usa el swipe, en vez de navegar por su cuenta. Ése es el PUENTE.
 *   · EL PROPIO HOOK, que marca los estados y expone el puente.
 *
 * ── POR QUÉ UN MÓDULO PLANO Y NO UN CONTEXTO ────────────────────────────────
 * CERO React y CERO DOM, a propósito y con tres razones:
 *   1. El curador es un observador externo que no importa un solo componente
 *      del carril (su restricción nº 1, ver su encabezado). Un contexto lo
 *      obligaría a montarse dentro del árbol.
 *   2. Esto se escribe en cada `touchmove`. Un estado de React ahí sería un
 *      re-render por píxel recorrido, que es exactamente lo que el gesto evita
 *      escribiendo directo al DOM.
 *   3. Se lee desde temporizadores y desde un `MutationObserver`, o sea fuera
 *      de cualquier render.
 *
 * ── LOS TRES ESTADOS ────────────────────────────────────────────────────────
 *   'reposo'  — nadie está tocando el carril. Es el estado inicial y al que se
 *               vuelve cuando la limpieza ocurre DE VERDAD (`limpiarRiel`).
 *   'gesto'   — un dedo se declaró dueño del riel y lo está pintando.
 *   'settle'  — el dedo soltó y hay un acomodo en vuelo (los DOS: el
 *               deslizamiento del commit y el resorte de la cancelación).
 *
 * `desde` es el reloj de pared del último cambio o refresco. NO es un contador
 * de sesión ni un identificador: sólo sirve para que quien lea pueda decidir si
 * la marca está FRESCA. Los topes de frescura los pone cada lector (el curador
 * los declara en su archivo) y son anti-atoro: un estado que se quedara pegado
 * jamás puede desactivar a nadie para siempre.
 *
 * ── VIDA DEL MÓDULO ─────────────────────────────────────────────────────────
 * Singleton por carga de la app, como los demás módulos planos de la casa. No
 * hay reseteo: quien necesite arrancar limpio (las pruebas) llama
 * `marcarCarril('reposo')` y `soltarPuente()`, que es exactamente el estado con
 * el que nace.
 */

/* ── EL ESTADO DEL CARRIL ─────────────────────────────────────────────────── */

let estadoActual = 'reposo';
let desdeActual = 0;

/** Fija el estado y sella la hora. Lo llaman el hook (al tomar posesión del
 *  riel, al arrancar un acomodo y al limpiar de verdad) y el curador (después
 *  de planchar, que es una limpieza de pleno derecho). */
export function marcarCarril(estado) {
  estadoActual = estado;
  desdeActual = Date.now();
}

/** Sólo mueve la hora, sin tocar el estado: el dedo sigue ahí. Va en cada
 *  `touchmove` del gesto dueño, y es lo que impide que un arrastre largo
 *  envejezca hasta cruzar el tope de frescura de quien lo lea. */
export function refrescarCarril() {
  desdeActual = Date.now();
}

/** Lo que hay, en un objeto nuevo: nadie puede mutar el estado por accidente. */
export function leerCarril() {
  return { estado: estadoActual, desde: desdeActual };
}

/* ── LA BANDERA DEL CLICK PROGRAMÁTICO ────────────────────────────────────── */

/* POR QUÉ UNA BANDERA Y NO `event.isTrusted`. La píldora necesita distinguir el
   tap de un dedo del `click()` que dispara código —el desenlace del swipe, o la
   liberación de la cola—, porque el primero hay que encolarlo y el segundo YA
   pasó por la cola. `isTrusted` no sirve como única fuente: en jsdom TODO evento
   nace no-confiable, así que la regla no se podría fijar en rojo. La bandera es
   determinista, se pone y se quita alrededor de un `click()` SÍNCRONO (no hay un
   solo `await` entre las dos escrituras) y es la misma en el aparato y en la
   prueba. `MobileBottomNav` conserva aparte su propio discriminador de SOMBRAS
   del navegador (`esSombraDelNavegador`), que resuelve otro problema. */
let clickProgramaticoActivo = false;

export function setClickProgramatico(valor) {
  clickProgramaticoActivo = !!valor;
}

export function esClickProgramatico() {
  return clickProgramaticoActivo;
}

/* ── EL PUENTE A LA COLA DE UNO ───────────────────────────────────────────── */

/* La cola de uno (#183) vive en refs dentro de `lib/useTabSwipe.js`: es de esa
   instancia y no se puede leer desde fuera. El puente es el mínimo asomo que
   hace falta para que la píldora meta su tap en ESA cola y no en una segunda
   —dos colas serían dos verdades sobre qué navegación va en vuelo—.

   Lo registra el hook cuando engancha su nodo y lo suelta en `detach`, así que
   fuera de la app (o con el carril desmontado) `puente()` devuelve null y la
   píldora se comporta EXACTAMENTE como antes de esta tanda: el `<Link>` navega
   solo. Falla cerrado, que es como tiene que fallar un atajo. */
let puenteVivo = null;

/** @param {{hayVuelo: function(): boolean, encolar: function(function): boolean}} api */
export function registrarPuente(api) {
  puenteVivo = api || null;
}

export function soltarPuente() {
  puenteVivo = null;
}

export function puente() {
  return puenteVivo;
}
